/**
 * creatorModalsShared — utilities + style helpers + constants used by the
 * three creator modals (Offline / LiveStream / PracticeUpload). Extracted
 * from CreatorModals.tsx (AMB-232 slice 1) so the three modals can move
 * to their own files without each one duplicating the ~150 lines of
 * shared scaffolding that grew up around them.
 *
 * Nothing in here is product logic — just byte/throughput/eta formatters
 * for the upload progress row, button/input style factories, and a
 * handful of date helpers for the start-time validation. The modals
 * import what they need.
 */
import { Platform } from 'react-native';

export const ACCENT = '#E98245';

/**
 * Human-readable byte size (RU locale). 1.68 ГБ, 43.2 МБ, 87 КБ.
 * Used by the PracticeUploadModal progress row — see AMB-225 FE doc § 2.
 */
export function formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return '—';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;
    if (gb >= 1) return `${gb.toFixed(2)} ГБ`;
    if (mb >= 1) return `${mb.toFixed(1)} МБ`;
    if (kb >= 1) return `${Math.round(kb)} КБ`;
    return `${Math.round(bytes)} Б`;
}

/** Bytes/sec → "3.4 МБ/с" / "120 КБ/с". `null` returns "—". */
export function formatThroughput(bps: number | null): string {
    if (bps === null || !Number.isFinite(bps) || bps <= 0) return '—';
    return `${formatBytes(bps)}/с`;
}

/**
 * Seconds → "осталось ~6 мин" / "осталось ~45 сек" / "осталось ~1 ч 20 мин".
 * `null` returns empty string so the parent can hide the chunk entirely
 * during the first window (before throughput is known).
 */
export function formatEta(seconds: number | null): string {
    if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '';
    if (seconds < 60) return `осталось ~${Math.max(1, Math.round(seconds))} сек`;
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `осталось ~${mins} мин`;
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins > 0 ? `осталось ~${hours} ч ${remMins} мин` : `осталось ~${hours} ч`;
}

export const fieldLabel = (color: string) => ({
    fontSize: 13,
    fontWeight: '700' as const,
    color,
    letterSpacing: 0.3,
    marginBottom: 8,
});

export const inputStyle = (isDark: boolean, tc: any) => ({
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: tc.text,
});

// `isDark` is optional so existing callers compile, but pass it whenever
// the button can render on a dark surface — the original disabled colors
// (`#CBD5E0` light gray, `#FFC9A8` peach) were designed for the light
// canvas only and looked like "white" buttons in dark theme.
export const primaryBtn = (disabled: boolean, isDark = false) => ({
    backgroundColor: disabled
        ? (isDark ? 'rgba(255,255,255,0.08)' : '#CBD5E0')
        : '#0F1419',
    borderRadius: 14,
    paddingVertical: 16,
    alignItems: 'center' as const,
    marginTop: 8,
    ...(Platform.OS === 'web' && { cursor: disabled ? 'not-allowed' : 'pointer' } as any),
});

export const orangeBtn = (disabled: boolean, isDark = false) => ({
    backgroundColor: disabled
        ? (isDark ? 'rgba(233, 130, 69,0.32)' : '#FFC9A8')
        : ACCENT,
    borderRadius: 14,
    paddingVertical: 18,
    alignItems: 'center' as const,
    flexDirection: 'row' as const,
    justifyContent: 'center' as const,
    gap: 10,
    flex: 1,
    ...(Platform.OS === 'web' && { cursor: disabled ? 'not-allowed' : 'pointer', boxShadow: disabled ? 'none' : '0 8px 24px rgba(233, 130, 69,0.25)' } as any),
});

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Returns YYYY-MM-DD in the LOCAL timezone — drop-in replacement for
 *  `.toISOString().slice(0, 10)` which uses UTC and silently rolls the
 *  date by ±1 day for events near local midnight. The bug surfaced when
 *  a master clicked the pencil-edit on an evening announcement and the
 *  modal hydrated with «day later» / «day earlier» depending on tz. */
export const localISODate = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const localHHMM = (d: Date): string => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

export const todayISO = () => localISODate(new Date());

/** Live broadcasts must be scheduled at least this many minutes ahead so
 *  invited guests have time to prepare (open the link, grant cam/mic
 *  permissions, frame themselves). Offline events have no such lead-time
 *  requirement — masters can announce a meet-up «right now». */
export const MIN_LIVE_LEAD_MINUTES = 5;

/** Smart default for the date/time fields when the modal opens (or after
 *  reset). Returns the next 15-minute mark at least `bufferMin` minutes
 *  from now in the LOCAL timezone. Replaces the legacy hardcoded
 *  `today + 19:00` default which silently put events in the past for any
 *  master opening the modal after 7pm. */
export const nextValidStart = (bufferMin = 0): { date: string; time: string } => {
    const d = new Date(Date.now() + bufferMin * 60 * 1000);
    const roundedMin = Math.ceil(d.getMinutes() / 15) * 15;
    // setMinutes(60, ...) overflows to next hour — that's the desired behavior
    // around xx:46-xx:59.
    d.setMinutes(roundedMin, 0, 0);
    return { date: localISODate(d), time: localHHMM(d) };
};

/** Returns ms timestamp; 0 if invalid date/time. */
export const parseStartMs = (date: string, time: string): number => {
    if (!date) return 0;
    const t = time || '00:00';
    const ms = new Date(`${date}T${t}:00`).getTime();
    return Number.isFinite(ms) ? ms : 0;
};

/** Verifies the start datetime is in the future. No buffer — used for
 *  offline events (master can announce a meet-up that started seconds
 *  ago for spontaneous sessions). Live broadcasts use the buffered
 *  variant below. */
export const isFutureStart = (date: string, time: string): boolean => {
    const ms = parseStartMs(date, time);
    return ms > 0 && ms > Date.now();
};

/** Same as isFutureStart but with a buffer — used for live broadcasts so
 *  invited guests have time to prepare before the master goes live. The
 *  buffer is enforced at submit time; the date/time picker also uses
 *  `nextValidStart(MIN_LIVE_LEAD_MINUTES)` as its default so the master
 *  rarely sees the validation error in practice. */
export const isFutureStartWithBuffer = (date: string, time: string, bufferMin: number): boolean => {
    const ms = parseStartMs(date, time);
    return ms > 0 && ms >= Date.now() + bufferMin * 60 * 1000;
};

export const PUBLISH_DISCLAIMER =
    'Публикуя событие, вы соглашаетесь с правилами и этикой платформы Agapella: уважительное общение, отсутствие запрещённого контента и рекламы. Нарушения могут привести к удалению события или блокировке аккаунта.';

/** Edit → Preview → Success — the same three-step wizard both Offline
 *  and LiveStream modals walk through. */
export type LiveModalStep = 'edit' | 'design' | 'preview' | 'success';

/** «10 ноября, 14:30» — human-readable RU date for the preview card.
 *  Returns '' for invalid input so the parent's empty-string fallback
 *  («Без названия» style) doesn't render an «Invalid Date» token. */
export const formatPreviewDate = (date: string, time: string): string => {
    if (!date) return '';
    const d = new Date(`${date}T${(time || '00:00')}:00`);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toLocaleString('ru', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};
